'use client';

import type { ForwardedRef, TextareaHTMLAttributes } from 'react';
import { forwardRef } from 'react';
import styled from 'styled-components';

import { Error } from '@zoomit/icons';
import type { SpacingType } from '@zoomit/primitives';
import { Spacing, spacingHelper } from '@zoomit/primitives';

import { cssVar } from '../../css/css-var';
import { theme } from '../../theme';

type TextAreaBaseProps = {
  ref?: ForwardedRef<HTMLTextAreaElement>;
  error?: boolean;
  spacing?: SpacingType;
};

const Root = styled.div<TextAreaBaseProps>`
  width: 100%;
  position: relative;
  height: 100%;

  & .error {
    left: 1.2rem;
    right: unset;
  }
`;

const TextAreaBase = styled.textarea<TextAreaBaseProps>`
  width: 100%;
  padding: ${Spacing.x}rem;
  border: 1px solid var(${theme.grey3});
  border-radius: var(${theme.radius.sm});
  font-family: inherit;
  font-size: 1.4rem;
  resize: none;
  ${({ spacing }) => spacing && spacingHelper(spacing)};

  &:focus {
    ${({ error }) => error && `outline: none`};
    outline: 1px solid #2e2d2d;
  }

  &::placeholder {
    color: var(${theme.grey3});
  }
`;

export type TextAreaProps = TextareaHTMLAttributes<HTMLTextAreaElement> & TextAreaBaseProps;

export const TextArea = forwardRef((props: TextAreaProps, ref: ForwardedRef<HTMLTextAreaElement>) => {
  const { error, ...rest } = props;

  return (
    <Root>
      <TextAreaBase ref={ref} {...rest} error={error} />
      {error && (
        <i className="icon error">
          <Error fill={cssVar(theme.primary1)} />
        </i>
      )}
    </Root>
  );
});
